'use client'

import * as React from 'react'
import { CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner'
import { useTheme } from '../../providers/theme-provider'
import { activateOnKey } from '../../lib/activate-on-key'

// ── Types (kept stable for backward compat) ──────────────────────────────────
export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

// ── Imperative helper ────────────────────────────────────────────────────────
// Backed by sonner. The signature mirrors the previous homegrown toast so
// existing call sites — `toast(msg)`, `toast.success(msg)`, `toast.error(msg)`,
// `toast(msg, 'info', 0)` for a sticky toast — keep working unchanged.

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5" aria-hidden="true" />,
  error: <AlertCircle className="w-5 h-5" aria-hidden="true" />,
  info: <Info className="w-5 h-5" aria-hidden="true" />,
  warning: <AlertTriangle className="w-5 h-5" aria-hidden="true" />,
}

/** Map the legacy `duration` arg to sonner: `0` means "stay until dismissed". */
function toDuration(duration?: number): number | undefined {
  if (duration === undefined) return undefined
  return duration <= 0 ? Infinity : duration
}

function show(message: string, type: ToastType, duration?: number): string | number {
  const opts = { icon: icons[type], duration: toDuration(duration) }
  switch (type) {
    case 'success':
      return sonnerToast.success(message, opts)
    case 'error':
      return sonnerToast.error(message, opts)
    case 'warning':
      return sonnerToast.warning(message, opts)
    case 'info':
    default:
      return sonnerToast.info(message, opts)
  }
}

/**
 * Rich toast options for content-heavy toasts (e.g. an incoming
 * notification): a title with secondary description, custom leading icon,
 * a click-through handler, and an optional action button. Keeps sonner's
 * surface contained inside @rr/ui so apps never import sonner directly.
 */
export interface RichToastOptions {
  description?: React.ReactNode
  icon?: React.ReactNode
  duration?: number
  /** Fired when the toast body is clicked. */
  onClick?: () => void
  /** Trailing action button. */
  action?: { label: string; onClick: () => void }
}

/**
 * Custom-rendered toast body for `toast.message`. Styled to match sonner's
 * default toast (RR tokens) and fully clickable when `onClick` is provided.
 */
export function RichToast({
  id,
  title,
  description,
  icon,
  onClick,
  action,
}: {
  id: string | number
  title: string
  description?: React.ReactNode
  icon?: React.ReactNode
  onClick?: () => void
  action?: { label: string; onClick: () => void }
}) {
  const activate = onClick
    ? () => {
        onClick()
        sonnerToast.dismiss(id)
      }
    : undefined
  const clickable = Boolean(activate)
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={activate}
      onKeyDown={activate ? activateOnKey(activate) : undefined}
      className={[
        'flex w-full items-start gap-3 rounded-lg border border-border bg-popover',
        'px-4 py-3 text-popover-foreground shadow-lg',
        clickable
          ? 'cursor-pointer transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          : '',
      ].join(' ')}
    >
      {icon ? <div className="mt-0.5 shrink-0 text-muted-foreground">{icon}</div> : null}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight">{title}</p>
        {description ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            action.onClick()
            sonnerToast.dismiss(id)
          }}
          className="shrink-0 self-center rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
        >
          {action.label}
        </button>
      ) : null}
    </div>
  )
}

interface ToastFn {
  (message: string, type?: ToastType, duration?: number): string | number
  success: (message: string, duration?: number) => string | number
  error: (message: string, duration?: number) => string | number
  info: (message: string, duration?: number) => string | number
  warning: (message: string, duration?: number) => string | number
  /** Neutral toast with rich content (title + description + icon + click). */
  message: (title: string, options?: RichToastOptions) => string | number
  /** Sticky toast; resolve/reject it via the returned id with `toast.dismiss`. */
  loading: (message: string, duration?: number) => string | number
  /** Dismiss a specific toast (or all when called with no id). */
  dismiss: (id?: string | number) => void
  /** Tie a toast's lifecycle to a promise (sonner native). */
  promise: typeof sonnerToast.promise
}

/**
 * Imperative toast helper. Safe to call from anywhere — components, event
 * handlers, async code (no-op on the server). Now backed by sonner.
 */
export const toast = ((message: string, type: ToastType = 'info', duration?: number) =>
  show(message, type, duration)) as ToastFn

toast.success = (message, duration) => show(message, 'success', duration)
toast.error = (message, duration) => show(message, 'error', duration)
toast.info = (message, duration) => show(message, 'info', duration)
toast.warning = (message, duration) => show(message, 'warning', duration)
toast.message = (title, options = {}) => {
  const { description, icon, duration, onClick, action } = options
  return sonnerToast.custom(
    (id) => (
      <RichToast
        id={id}
        title={title}
        description={description}
        icon={icon}
        onClick={onClick}
        action={action}
      />
    ),
    { duration: toDuration(duration) },
  )
}
toast.loading = (message, duration) => sonnerToast.loading(message, { duration: toDuration(duration ?? 0) })
toast.dismiss = (id) => sonnerToast.dismiss(id)
toast.promise = sonnerToast.promise.bind(sonnerToast)

// ── Container ────────────────────────────────────────────────────────────────
export interface ToastContainerProps {
  /** Anchor position. Defaults to bottom-right (platform convention). */
  position?: React.ComponentProps<typeof SonnerToaster>['position']
  /** Optional extra classes forwarded to sonner's Toaster wrapper. */
  className?: string
}

/**
 * Render once near the root of the app. Subsequent `toast()` calls render here.
 *
 * Wraps sonner's `<Toaster />`, themed from the RR design tokens so toasts
 * match light/dark mode and the platform accent automatically.
 */
export function ToastContainer({ position = 'bottom-right', className }: ToastContainerProps = {}) {
  // Mounted inside <ThemeProvider> in every app's root, so this is safe.
  const { resolvedTheme } = useTheme()

  return (
    <SonnerToaster
      theme={resolvedTheme}
      position={position}
      richColors
      closeButton
      className={className}
      toastOptions={{
        classNames: {
          toast: 'rounded-lg shadow-lg',
        },
      }}
    />
  )
}
